import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { PermissionSetBuilderPage } from '@/app/(client)/company-settings/-components/permissionSets/PermissionSetBuilderPage';
import { ensurePermissions } from '@/hooks/usePermissions';
import { PLATFORM_FEATURES } from '@/lib/constants/features';
import { hasFeature } from '@/lib/utils/features';
import { bootstrapSession } from '@/lib/utils/session';
import { useSessionStore } from '@/store/sessionStore';

// Who the new set is being made for. Carried in the URL rather than in memory
// because the Manage-access sheet that starts this flow closes on the way.
const searchSchema = z.object({
  assignTo: z.array(z.string()).optional(),
});

export const Route = createFileRoute(
  '/(client)/company-settings/(pages)/permission-sets/new',
)({
  validateSearch: searchSchema,
  // Withholding the Create button is not access control: this page is
  // reachable by URL, and without the guard someone could author a whole set
  // and only discover at Save that `POST /roles` answers 403. Authoring custom
  // sets is the part that is sold; reading and editing the predefined ones is
  // not, so this sends them back to the list rather than off the page.
  beforeLoad: async () => {
    const user = useSessionStore.getState().user ?? (await bootstrapSession());
    const perms = await ensurePermissions();
    // Both, because `POST /roles` checks both: the enterprise feature says the
    // company may author sets at all, `permission-access:create` says this
    // caller may. An admin who grants a Creator only `edit` leaves them here
    // with nothing to save.
    if (
      !hasFeature(user, PLATFORM_FEATURES.PERMISSIONS_CUSTOM_ROLES) ||
      !perms['permission-access'].canCreate
    ) {
      throw redirect({ to: '/company-settings/permission-sets' });
    }
  },
  component: NewPermissionSetRoute,
});

function NewPermissionSetRoute() {
  const { assignTo } = Route.useSearch();
  const companyId = useSessionStore(s => s.user?.companyId);
  if (!companyId) return null;
  return <PermissionSetBuilderPage companyId={companyId} assignTo={assignTo} />;
}
